import { defineMessages, useIntl } from 'react-intl';

import type { Route } from '@confluence/route/entry-points/Route.ts';
import {
	SPACE_PERMISSIONS_SETTINGS,
	ADMIN_COMPANY_HUB_PERMISSIONS,
} from '@confluence/named-routes/entry-points/routes.ts';
import { SpacePermissionsTabKey } from '@confluence/space-permissions/entry-points/spacePermissionsKeys.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useSpaceManagementOperations } from '@confluence/space-utils/entry-points/graphql.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { KeysOfWebItemsWithExtraUrls } from '../helpers/webItem.ts';

import type {
	SpaceSettingsWebItemSectionsFragment_webItemSections,
	SpaceSettingsWebItemSectionsFragment_webItemSections_items,
} from './__types__/SpaceSettingsWebItemSectionsFragment.ts';
import type { SpaceSettingsWebItemSectionsInjector } from './SpaceSettingsWebItemSectionsInjector.ts';

export const SPACE_PERMISSIONS_WEBITEM_SECTION_ID = '-system.space.tools-permissions';
const SPACE_PERMISSIONS_COMPLETE_KEY = 'confluence-frontend.';
const USERS_TAB_ID = 'space-permissions-users';
const GROUPS_TAB_ID = 'space-permissions-groups';
const GUESTS_TAB_ID = 'space-permissions-guests';
const ANONYMOUS_TAB_ID = 'space-permissions-anonymous-access';

const { GENERAL_PERMISSIONS_WEBITEM_COMPLETE_KEY } = KeysOfWebItemsWithExtraUrls;

const i18n = defineMessages({
	spacePermissionsWebItemUsersLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.users',
		defaultMessage: 'Users',
		description: 'Label for the web item link for the users tab',
	},
	spacePermissionsWebItemGroupsLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.groups',
		defaultMessage: 'Groups',
		description: 'Label for the web item link for the groups tab',
	},
	spacePermissionsWebItemTeamsAndGroupsLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.teamsgroups',
		defaultMessage: 'Teams and groups',
		description: 'Label for the web item link for the groups tab',
	},
	spacePermissionsWebItemGuestsLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.guests',
		defaultMessage: 'Guests',
		description: 'Label for the web item link for the guests tab',
	},
	spacePermissionsWebItemAnonymousAccessLink: {
		id: 'space-settings.web-item-section.space-permissions.web-item.anonymous-access',
		defaultMessage: 'Anonymous access',
		description: 'Label for the web item link for the anonymous access tab',
	},
	hubPermissionsWebItemSectionTitle: {
		id: 'space-settings.web-item-section.hub-permissions.web-item.title',
		defaultMessage: 'Hub permissions',
		description: 'Label for the hub permissions web item section',
	},
});

const addSpacePermissionsWebItems = (
	itemsToAdd: SpaceSettingsWebItemSectionsFragment_webItemSections_items[],
	section: SpaceSettingsWebItemSectionsFragment_webItemSections | null,
): SpaceSettingsWebItemSectionsFragment_webItemSections | null => {
	if (!section || !itemsToAdd?.length) {
		return section;
	}
	//remove the "General" link and add the rest of the space permissions tabs
	const isGeneralLink = (
		item: SpaceSettingsWebItemSectionsFragment_webItemSections_items | null | undefined,
	) => item?.completeKey == GENERAL_PERMISSIONS_WEBITEM_COMPLETE_KEY;
	const generalLinkIndex = section.items.findIndex(isGeneralLink);

	if (generalLinkIndex !== -1) {
		// XXX Don't mutate the input section! May be shared across invocations.
		const items = [...section.items];
		items.splice(generalLinkIndex, 1, ...itemsToAdd);
		return {
			...section,
			items,
		} as unknown as SpaceSettingsWebItemSectionsFragment_webItemSections;
	}
	return section;
};

export const useSpacePermissionsWebItems: SpaceSettingsWebItemSectionsInjector = ({
	webItemSections,
	isSpaceAdmin,
}) => {
	const spaceKey = useSpaceKey();
	const sectionIndex =
		webItemSections?.findIndex(
			(section: SpaceSettingsWebItemSectionsFragment_webItemSections | null | undefined) =>
				section?.id === SPACE_PERMISSIONS_WEBITEM_SECTION_ID,
		) ?? -1;
	const inputSection = webItemSections?.[sectionIndex] ?? null;
	const { isRbacEnabled } = useSpaceRoleMode();
	const { canManageNonlicensedUsers, canManageUsers } = useSpaceManagementOperations({
		spaceKey: spaceKey ?? '',
		skip:
			isRbacEnabled || expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false)
				? false
				: undefined,
	});
	const outputSection = useSpacePermissionsWebItemSection(
		spaceKey,
		inputSection,
		isSpaceAdmin,
		isRbacEnabled,
		canManageNonlicensedUsers,
		canManageUsers,
	);

	if (inputSection && inputSection !== outputSection && webItemSections) {
		// XXX Don't mutate the input webItemSections! May be shared across invocations.
		const result = [...webItemSections];
		result[sectionIndex] = outputSection;
		return result;
	}

	return webItemSections;
};

const ANALYTICS_PLUGIN_WEB_ITEM_COMPLETE_KEY =
	'com.atlassian.plugins.atlassian-connect-plugin:com.addonengine.analytics__analytics-permissions-space-tools-tab';
const ADDON_SPACE_PERMISSIONS_COMPLETE_KEY =
	'confluence.sections.space.tools.v2:addonspacepermissions';

function useSpacePermissionsWebItemSection(
	spaceKey: string | null | undefined,
	section: SpaceSettingsWebItemSectionsFragment_webItemSections | null,
	isSpaceAdmin: boolean,
	isRbacEnabled: boolean,
	canManageNonlicensedUsers: boolean,
	canManageUsers: boolean,
): SpaceSettingsWebItemSectionsFragment_webItemSections | null {
	const intl = useIntl();
	if (!section) return section;

	let result: SpaceSettingsWebItemSectionsFragment_webItemSections = section;
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	if (isCompanyHub) {
		// Company Hub hides its space from the user so the label/title "Space
		// permissions" is undesirable.
		result = {
			...result,
			label: intl.formatMessage(i18n.hubPermissionsWebItemSectionTitle),
		} as unknown as SpaceSettingsWebItemSectionsFragment_webItemSections;
	}

	if (!spaceKey) {
		return result;
	}

	const getPermissionSettingsWebItems = (
		route: Route,
	): SpaceSettingsWebItemSectionsFragment_webItemSections_items[] => [
		{
			id: USERS_TAB_ID,
			label: intl.formatMessage(i18n.spacePermissionsWebItemUsersLink),
			url: route.toUrl({
				spaceKey,
				tab: SpacePermissionsTabKey.Users,
			}),
			completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + USERS_TAB_ID,
			icon: null,
		},
		{
			id: GROUPS_TAB_ID,
			label: fg('confluence_frontend_perms_exp_teams_as_principals')
				? intl.formatMessage(i18n.spacePermissionsWebItemTeamsAndGroupsLink)
				: intl.formatMessage(i18n.spacePermissionsWebItemGroupsLink),
			url: route.toUrl({
				spaceKey,
				tab: SpacePermissionsTabKey.Groups,
			}),
			completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + GROUPS_TAB_ID,
			icon: null,
		},
		{
			id: GUESTS_TAB_ID,
			label: intl.formatMessage(i18n.spacePermissionsWebItemGuestsLink),
			url: route.toUrl({
				spaceKey,
				tab: SpacePermissionsTabKey.Guests,
			}),
			completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + GUESTS_TAB_ID,
			icon: null,
		},
		{
			id: ANONYMOUS_TAB_ID,
			label: intl.formatMessage(i18n.spacePermissionsWebItemAnonymousAccessLink),
			url: SPACE_PERMISSIONS_SETTINGS.toUrl({
				spaceKey,
				tab: SpacePermissionsTabKey.AnonymousAccess,
			}),
			completeKey: SPACE_PERMISSIONS_COMPLETE_KEY + ANONYMOUS_TAB_ID,
			icon: null,
		},
	];

	const itemsToAdd = getPermissionSettingsWebItems(
		isCompanyHub ? ADMIN_COMPANY_HUB_PERMISSIONS : SPACE_PERMISSIONS_SETTINGS,
	);

	const nextSection = addSpacePermissionsWebItems(itemsToAdd, result);
	result = nextSection ?? result;

	// Filter out analytics permissions item: when experiment use MANAGE_USERS (canManageUsers), else space admin
	const canSeeAnalytics = expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false)
		? canManageUsers
		: isSpaceAdmin;
	if (!canSeeAnalytics) {
		result = {
			...result,
			items: result!.items?.filter(
				(item) => item?.completeKey !== ANALYTICS_PLUGIN_WEB_ITEM_COMPLETE_KEY,
			),
		} as unknown as SpaceSettingsWebItemSectionsFragment_webItemSections;
	}

	// Apps: always shown
	const canSeeApps = expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false)
		? canManageUsers
		: true;
	result = {
		...result,
		items: result!.items
			?.filter((item) => item?.completeKey !== ADDON_SPACE_PERMISSIONS_COMPLETE_KEY || canSeeApps)
			?.map((item) => {
				if (item?.completeKey === ADDON_SPACE_PERMISSIONS_COMPLETE_KEY && canSeeApps) {
					return {
						...item,
						url: SPACE_PERMISSIONS_SETTINGS.toUrl({
							spaceKey,
							tab: SpacePermissionsTabKey.Apps,
						}),
					};
				}
				return item;
			}),
	} as unknown as SpaceSettingsWebItemSectionsFragment_webItemSections;

	if (isCompanyHub) {
		// Only keep Users, Groups, and Guests tabs for Company Hub.
		const allowedTabs = [USERS_TAB_ID, GROUPS_TAB_ID, GUESTS_TAB_ID];
		result = {
			...result,
			items: result!.items?.filter((item) => item?.id && allowedTabs.includes(item.id)),
		} as unknown as SpaceSettingsWebItemSectionsFragment_webItemSections;
	} else if (isRbacEnabled) {
		//if RBAC is enabled, we want to actually remove the users, groups, and guests tabs
		const excludedTabs = [USERS_TAB_ID, GROUPS_TAB_ID, GUESTS_TAB_ID];
		// Also exclude anonymous access tab if user doesn't have manage_nonlicensed_users permission
		if (!canManageNonlicensedUsers) {
			excludedTabs.push(ANONYMOUS_TAB_ID);
		}
		result = {
			...result,
			items: result!.items?.filter((item) => !item?.id || !excludedTabs.includes(item.id)),
		} as unknown as SpaceSettingsWebItemSectionsFragment_webItemSections;
	}
	return result;
}
